import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DiscussionsCommentV2 as DiscussionComment } from '@air/component-discussions';
import { XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { formatFullName, formatUpdatedAt, parseAirDateToISO } from '@air/utilities';
import { useCurrentUserSession } from '@air/utils-auth';

import { AnnotationReplyButton } from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/AnnotationAvatarContainer/components/AnnotationReplyButton';
import { type AnnotationCommentDisplayProps } from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/shared/annotationCommentTypes';
import { useDiscussionPanelContextSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { type DiscussionsPanelContextValue } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextTypes';
import { BOUNDING_BOX_AVATAR_SIZE } from '~/components/BoundingBox/components/BoundingBoxAvatar';
import { BOUNDING_BOX_TEXT_WIDTH } from '~/components/BoundingBox/constants';
import { CommentMenu } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/CommentMenu';
import { DiscussionCommentContent } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/DiscussionCommentContent';
import { ReopenDiscussionButton } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/ReopenDiscussionButton';
import { useCommentEditing } from '~/components/DiscussionsV2/Discussion/shared/useCommentEditing';
import { QueryParamNames } from '~/constants/search';
import { ANNOTATION_COMMENT_CONTAINER, CLOSE_FLOATING_COMMENT_BUTTON } from '~/constants/testIDs';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useAccountContext } from '~/providers/AccountProvider';
import { centralizedBoardAncestorsSelector } from '~/store/centralizedBoard/selectors';
import { type DeleteCommentClick } from '~/swr-hooks/discussions/useDeleteCommentClick';
import { useGetDiscussionMentionsMembers } from '~/swr-hooks/discussions/useGetDiscussionMentionsMembers';
import { useResolveCommentClick } from '~/swr-hooks/discussions/useResolveCommentClick';
import { useSaveEditedCommentClick } from '~/swr-hooks/discussions/useSaveCommentEditClick';
import { isCommentAuthor } from '~/utils/comments';
import { setQueryParams } from '~/utils/PathUtils';

const AVATAR_HORIZONTAL_OFFSET_PX = 12;

export type AnnotationCommentProps = AnnotationCommentDisplayProps & {
  /**
   * The hosting surface's comment delete — bound by the per-surface wrappers
   * (PrivateAnnotationComment / PublicAnnotationComment) so this shared body stays surface-blind
   * about which API family deletes the comment.
   */
  onDeleteComment: DeleteCommentClick;
};

export const ANNOTATION_COMMENT_ID = 'annotation-comment';

export const AnnotationComment = memo(
  ({
    discussion,
    position,
    canEdit,
    canResolve,
    canDelete,
    canReply,
    canShowMentionsButton,
    onDeleteComment,
  }: AnnotationCommentProps) => {
    const boardId = useURLBoardIdSelector();
    const ancestors = useSelector(centralizedBoardAncestorsSelector);
    const firstComment = discussion.comments.data[0];
    const { data: account } = useAccountContext();
    const { data: userSession } = useCurrentUserSession();
    const isAuthor = isCommentAuthor({ comment: firstComment, account, userSession });

    const { enableEditing, isEditing, disableEditing } = useCommentEditing();

    const { handleResolveCommentClick } = useResolveCommentClick({ trackLocation: 'asset-page' });
    const { handleSaveEditedCommentClick } = useSaveEditedCommentClick({ trackLocation: 'asset-page' });

    const isReplyingCurrentDiscussionIdSelector = useCallback(
      (state: DiscussionsPanelContextValue) => state.replyingDiscussionId === discussion.id,
      [discussion.id],
    );

    const isAddingReply = useDiscussionPanelContextSelector(isReplyingCurrentDiscussionIdSelector);

    const { mentionMembers, isLoadingMentions } = useGetDiscussionMentionsMembers({
      boardId,
      ancestors,
      workspaceId: discussion.workspaceId,
    });

    const positionValue = BOUNDING_BOX_AVATAR_SIZE + AVATAR_HORIZONTAL_OFFSET_PX;
    const horizontalPos = useMemo(
      () => (position.includes('left') ? { right: positionValue } : { left: positionValue }),
      [position, positionValue],
    );

    const onSaveClick = useCallback(
      (newComment: string) => {
        // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
        handleSaveEditedCommentClick({
          newComment,
          comment: firstComment,
          clipId: discussion.clipId,
          timestamp: discussion.timestamp,
          annotation: discussion.annotation,
        });
      },
      [handleSaveEditedCommentClick, firstComment, discussion],
    );

    const reopenButton = useMemo(
      () =>
        discussion.resolved && canResolve ? (
          <ReopenDiscussionButton onToggleResolve={handleResolveCommentClick} discussion={discussion} />
        ) : null,
      [discussion, canResolve, handleResolveCommentClick],
    );

    const commentMenu = useMemo(
      () =>
        discussion.resolved && canResolve ? null : ( // Re-open button moved to secondaryActions when resolved
          <div className="flex items-center">
            <CommentMenu
              comment={firstComment}
              discussion={discussion}
              onToggleResolve={canResolve ? handleResolveCommentClick : undefined}
              onDelete={canDelete && isAuthor ? onDeleteComment : undefined}
              onEdit={canEdit && isAuthor ? enableEditing : undefined}
            />
            <IconButton
              adsIcon
              data-testid={CLOSE_FLOATING_COMMENT_BUTTON}
              icon={XIcon}
              appearance="ghost"
              color="gray"
              size="small"
              // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
              onClick={() => setQueryParams({ [QueryParamNames.discussionId]: null }, 'replace')}
              label="Close annotation"
            />
          </div>
        ),
      [
        discussion,
        canResolve,
        handleResolveCommentClick,
        firstComment,
        canDelete,
        isAuthor,
        onDeleteComment,
        canEdit,
        enableEditing,
      ],
    );

    const finalSecondaryActions = useMemo(() => {
      if (reopenButton) {
        return reopenButton;
      }

      return canReply && !discussion.resolved && !isAddingReply ? (
        <AnnotationReplyButton discussion={discussion} />
      ) : null;
    }, [reopenButton, canReply, discussion, isAddingReply]);

    if (!firstComment) {
      return null;
    }

    return (
      <div
        className="rounded-8 ads:bg-base bg-gray-1 shadow-popover"
        id={ANNOTATION_COMMENT_ID}
        data-testid={ANNOTATION_COMMENT_CONTAINER}
        style={{
          ...horizontalPos,
          width: BOUNDING_BOX_TEXT_WIDTH,
        }}
      >
        <DiscussionComment
          className="px-4 py-3"
          actions={commentMenu}
          author={formatFullName(firstComment.account.firstName, firstComment.account.lastName)}
          comment={
            <DiscussionCommentContent
              isEditing={isEditing}
              cancelEditing={disableEditing}
              comment={firstComment}
              onSave={canEdit ? onSaveClick : undefined}
              isLoadingMentions={isLoadingMentions}
              mentionsList={mentionMembers}
              shouldShowMentionsButton={canShowMentionsButton}
            />
          }
          data-testid="ANNOTATION_COMMENT"
          timestamp={formatUpdatedAt(parseAirDateToISO(firstComment.createdAt).getTime())}
          secondaryActions={finalSecondaryActions}
        />
      </div>
    );
  },
);

AnnotationComment.displayName = 'AnnotationComment';
